/**
 * The formula behind the column you are standing in, always on screen.
 *
 * A spreadsheet puts this above the grid because a formula is a property of
 * the thing you are looking at, not a dialog you go and open. The modal is
 * still there for writing one from scratch; this is for reading and adjusting
 * the ones already on the sheet, which is what most of the time is spent on.
 *
 * A formula here belongs to a column rather than a cell, which is the one
 * place this differs from a spreadsheet and the reason the bar names the
 * column it is showing. Every row of that column is worked out the same way,
 * so there is nothing a per-cell formula would say that this does not.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Group, Paper, Popover, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconMathFunction } from '@tabler/icons-react';
import { useAddFormulaColumn, useCheckFormula, type SheetTransform } from '../api/queries';
import { TOKENS } from '../theme';

/** What the completions offer, beyond the file's own columns. */
const FUNCTIONS = [
  'IF', 'ROUND', 'CONCAT', 'UPPER', 'LOWER', 'LEFT', 'RIGHT', 'LEN', 'AND', 'OR',
];

/**
 * What is being typed at the caret, so the list can offer the right thing.
 *
 * A bracket that has been opened and not closed is a column being named;
 * letters not inside one are a function. Anything else offers nothing, because
 * a list that appears while you are typing a number is in the way.
 */
function wordAt(text: string, caret: number): { kind: 'column' | 'function'; word: string } | null {
  const before = text.slice(0, caret);
  const open = before.lastIndexOf('[');
  if (open > before.lastIndexOf(']')) {
    return { kind: 'column', word: before.slice(open + 1) };
  }
  const letters = /[A-Za-z]+$/.exec(before);
  return letters ? { kind: 'function', word: letters[0] } : null;
}

export function SheetFormulaBar({
  sheetId,
  columns,
  column,
  transforms,
  baseVersion,
  editable,
}: {
  sheetId: string | null;
  columns: string[];
  /** The column the selection is in, which is the one whose formula is shown. */
  column: string | null;
  transforms: SheetTransform[];
  baseVersion: number;
  editable: boolean;
}) {
  const saved = useMemo(
    () => transforms.find((t) => t.col === column && t.kind === 'formula')?.find ?? '',
    [transforms, column],
  );
  const [text, setText] = useState(saved);
  const [caret, setCaret] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const add = useAddFormulaColumn(sheetId);

  // Moving to another column shows that column's formula, dropping anything
  // half typed for the last one: it belonged to a column you have left.
  useEffect(() => setText(saved), [saved, column]);

  const [settled] = useDebouncedValue(text, 400);
  const check = useCheckFormula(sheetId, column ?? '', settled);
  const current = settled === text && !check.isFetching;
  const problem = current && check.data?.ok === false ? check.data.error : '';
  const sample = current && check.data?.ok ? (check.data.sample ?? []) : [];

  const suggestion = wordAt(text, caret);
  const options = useMemo(() => {
    if (!suggestion || suggestion.word.length === 0) return [];
    const lower = suggestion.word.toLowerCase();
    const pool = suggestion.kind === 'column' ? columns : FUNCTIONS;
    return pool.filter((o) => o.toLowerCase().startsWith(lower)).slice(0, 6);
  }, [suggestion, columns]);

  function accept(option: string) {
    if (!suggestion) return;
    const start = caret - suggestion.word.length;
    // A column completes to the end of its brackets and a function to its
    // opening one, so the next thing typed is the argument either way.
    const insert = suggestion.kind === 'column' ? `${option}]` : `${option}(`;
    const next = text.slice(0, start) + insert + text.slice(caret);
    setText(next);
    const at = start + insert.length;
    requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  }

  function save() {
    if (!column || !text.trim()) return;
    add.mutate(
      { name: column, formula: text, baseVersion },
      {
        onSuccess: () =>
          notifications.show({
            color: 'teal',
            title: `${column} filled in`,
            message: 'Worked out as the file is read. Apply to file to keep it.',
          }),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not use that formula',
            message: (e as Error).message,
          }),
      },
    );
  }

  if (!column) return null;
  const changed = text !== saved;

  return (
    <Paper withBorder p={6} radius="sm">
      <Group gap="xs" wrap="nowrap" align="center">
        <IconMathFunction size={15} style={{ flexShrink: 0, opacity: 0.6 }} />
        <Badge size="sm" variant="light" color="gray" style={{ flexShrink: 0, textTransform: 'none' }}>
          {column}
        </Badge>

        <Popover opened={options.length > 0} position="bottom-start" shadow="md" withinPortal>
          <Popover.Target>
            <input
              ref={input}
              value={text}
              disabled={!editable}
              placeholder={
                saved ? undefined : `Write a formula for ${column}, such as [price] * [qty]`
              }
              onChange={(e) => {
                setText(e.currentTarget.value);
                setCaret(e.currentTarget.selectionStart ?? 0);
              }}
              onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
              onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
              onKeyDown={(e) => {
                if (options.length > 0 && (e.key === 'Tab' || e.key === 'Enter')) {
                  e.preventDefault();
                  accept(options[0]!);
                  return;
                }
                if (e.key === 'Enter' && current && !problem) save();
                if (e.key === 'Escape') setText(saved);
                // The grid is listening for these, and a formula being typed
                // is not a reason to move the selection.
                e.stopPropagation();
              }}
              style={{
                flex: 1,
                minWidth: 0,
                border: `1px solid var(--mantine-color-${problem ? 'red-6' : 'default-border'})`,
                borderRadius: 4,
                padding: '4px 8px',
                fontFamily: 'var(--mantine-font-family-monospace)',
                fontSize: 12,
                background: 'var(--mantine-color-body)',
                color: 'inherit',
              }}
            />
          </Popover.Target>
          <Popover.Dropdown p={4}>
            {options.map((o, i) => (
              <Text
                key={o}
                size="xs"
                px={6}
                py={2}
                style={{ cursor: 'pointer', fontFamily: 'var(--mantine-font-family-monospace)' }}
                c={i === 0 ? undefined : 'dimmed'}
                onMouseDown={(e) => {
                  // Before blur, or the input loses the caret this reads.
                  e.preventDefault();
                  accept(o);
                }}
              >
                {o}
                {i === 0 && <Text span size="xs" c="dimmed"> · tab</Text>}
              </Text>
            ))}
          </Popover.Dropdown>
        </Popover>

        {problem && (
          <Text size="xs" c={TOKENS.redText} style={{ flexShrink: 0 }} lineClamp={1}>
            {problem}
          </Text>
        )}
        {!problem && sample.length > 0 && (
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }} lineClamp={1}>
            {/* What it works out to, on the rows at the top of the file. */}
            gives {sample.map((v) => (v === '' ? '(blank)' : v)).join(', ')}
          </Text>
        )}

        {editable && changed && (
          <Button
            size="compact-xs"
            loading={add.isPending}
            disabled={!current || !!problem || !text.trim()}
            onClick={save}
            style={{ flexShrink: 0 }}
          >
            {saved ? 'Update' : 'Fill column'}
          </Button>
        )}
      </Group>
    </Paper>
  );
}
